import React, { ChangeEvent, useEffect } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormFieldLayout,
  FormReadonlyField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { Alert } from 'antd'
import { NameDto } from 'api/Common/Types'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PaymentMethodCategory } from 'api/PaymentProvider/Types/PaymentMethodCategory'
import { PaymentMethodDto } from 'api/PaymentProvider/Types/PaymentMethodDto'
import { PaymentProviderFields } from 'api/PaymentProvider/Types/PaymentProviderFields'
import { PaymentProviderType } from 'api/PaymentProvider/Types/PaymentProviderType'
import { TransactionDto, WalletTransaction } from 'api/Transaction/Types/Transaction'
import { TransactionNoteDto } from 'api/Transaction/Types/TransactionNoteDto'
import { PaymentProviderAlternativeDto } from 'api/Wallet/Types/PaymentProviderAlternativeDto'
import { TransactionType } from 'api/Wallet/Types/TransactionTypeDto'
import { UpdateWalletDepositWithdrawalTransfer } from 'api/Wallet/Types/UpdateWalletDepositWithdrawalTransfer'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { CheckboxSwitch } from 'components/checkboxSwitch/CheckboxSwitch'
import { Link } from 'components/Link'
import { Select } from 'components/Select'
import { MainTemplate } from 'components/Templates/MainTemplate'
import { TransactionMenu } from 'components/TransactionMenu/TransactionMenu'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import omitEmpty from 'omit-empty'
import { WithdrawalFormValues } from 'routes/Transactions/Create/Withdrawal/WithdrawalForm'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'
import stringComparator from 'utils/stringComparator'
import {
  isNotAllowedToChangeState,
  isTransactionPendingStatus,
} from 'utils/transaction/status.index'
import { getCreditCreditOrFirstOneWallet } from 'utils/wallet'

import { TransactionDetailsTab } from '../DetailTabs'
import { filterTransactionNotes, NoteTextList } from '../NoteList'
import { ContentField } from '../TransactionDetailsFieldHelpers'

export interface TransactionDetailsWalletDepositWithdrawalFormFormValues {
  id: string
  referenceId: string
  amount: number
  comment: string | null
  wallet?: WalletDto
  walletTransaction?: WalletTransaction
  isBankTransaction: boolean
  isSkrillPaymentProviderType: boolean
  isWalletWithdrawal: boolean
  externalAccount?: string | null
  providerReference?: string | null
  providerResponse?: string | null
  paymentCurrencyId?: string | null
  updateDetails: { key: string; value: string | null; label: string }[]
  connectedToService?: boolean
  isOtherCurrency: boolean
  otherCurrency?: string
  currencyRate?: number
  otherAmount?: number
  otherAmountChecked?: boolean
  amountChecked?: boolean
  paymentProviderId?: string
  currency?: string
  bankAccountStatus?: NameDto
  // options: { [key: string]: string | number }
}

const useSetPaymentProviderOptions = (
  props: FormikProps<TransactionDetailsWalletDepositWithdrawalFormFormValues> & OuterProps
) => {
  const { paymentProviderFields, setFieldValue } = props

  useEffect(() => {
    if (paymentProviderFields.length) {
      const options = paymentProviderFields.reduce((previousValue, currentValue) => {
        if (currentValue.mandatory) {
          return {
            ...previousValue,
            [currentValue.field]: currentValue.systemFilled ? currentValue.value : '',
          }
        }
        return previousValue
      }, {})
      setFieldValue('options', options)
    }
  }, [paymentProviderFields]) // eslint-disable-line react-hooks/exhaustive-deps
}

const TransactionDetailsWalletDepositWithdrawalGeneralUI: React.FC<
  FormikProps<TransactionDetailsWalletDepositWithdrawalFormFormValues> & OuterProps
> = (props) => {
  const { values, resetForm, setFieldValue, notes, isEdit, setEdit } = props
  const { transaction, permissions, getPaymentProviderFields } = props
  const { setAuditLogModal, setCustomizeModal, setProviderValidationModal } = props
  const {
    sendBankStatementNeededCCCoveredIBEmailRequest,
    sendBankStatementNeededDepositConfirmationEmailRequest,
  } = props

  useSetPaymentProviderOptions(props)

  const handleSubmitForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    props.handleSubmit()
  }

  const handleCancel = () => {
    props.setEdit(false)
    resetForm()
  }

  const transactionNotes = filterTransactionNotes(notes)
  const paymentProviderOptions = props.alternativesPaymentProviders.map((paymentProvider) => ({
    key: paymentProvider.id,
    value: paymentProvider.id,
    label: paymentProvider.name,
    text: paymentProvider.name,
  }))
  const selectedPaymentProviderName =
    props.alternativesPaymentProviders.find(
      (paymentProvider) => paymentProvider.id === values.paymentProviderId
    )?.name ??
    props.paymentProviders.find(
      (paymentProvider) => paymentProvider.id === values.paymentProviderId
    )?.name ??
    ''

  return (
    <MainTemplate>
      <div className='px-4 md:px-6'>
        <Form onSubmit={handleSubmitForm}>
          <div className='flex flex-col gap-4'>
            {notes.length > 0 && (
              <Alert
                type='error'
                message={
                  <>
                    There are notes related to the client(s) involved in this transaction. To view
                    please
                    <Button
                      type='button'
                      variant='link'
                      onClick={() => props.setActiveTab(TransactionDetailsTab.NOTES)}
                    >
                      Click Here
                    </Button>
                  </>
                }
              />
            )}

            <DataGrid
              rightAddon={
                <ButtonGroup>
                  {!isEdit ? (
                    <Button
                      type='button'
                      onClick={() => setEdit(true)}
                      disabled={
                        !hasPermission(permissions, PermissionsEnum.CanEditTransactions) ||
                        isTransactionPendingStatus(transaction.state.id) ||
                        isNotAllowedToChangeState(transaction.state.id)
                      }
                      data-test='edit-button'
                    >
                      Edit
                    </Button>
                  ) : (
                    <>
                      <Button type='button' variant='destructive' onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button type='submit' disabled={props.isSubmitting}>
                        Save
                        {props.isSubmitting && <Spinner data-icon='inline-start' />}
                      </Button>
                    </>
                  )}
                  <TransactionMenu
                    permissions={permissions}
                    handleAuditLog={() => setAuditLogModal(true)}
                    handleCustomize={() => setCustomizeModal(true)}
                    handleProviderValidation={() => setProviderValidationModal(true)}
                    sendBankStatementNeededCCCoveredIBEmailRequest={
                      sendBankStatementNeededCCCoveredIBEmailRequest
                    }
                    sendBankStatementNeededDepositConfirmationEmailRequest={
                      sendBankStatementNeededDepositConfirmationEmailRequest
                    }
                    isSkrillPaymentProviderType={values.isSkrillPaymentProviderType}
                    isWalletWithdrawalType={values.isWalletWithdrawal}
                    isAdminCustomizationAvailable
                    isBankPaymentProviderType={values.isBankTransaction}
                  />
                </ButtonGroup>
              }
            >
              <DataColumn>
                <DataCard title='Transaction Overview'>
                  <FormReadonlyField label='Type' value={transaction.type.name} />
                  <FormReadonlyField
                    label='Date Created'
                    value={formatLocalDateUTC(transaction.createdDate, true)?.toString()}
                  />
                  {props.client && (
                    <FormReadonlyField
                      label='Client'
                      value={props.client.name}
                      link={generatePath(links.recordsClients.detailsUrl as string, {
                        id: props.client.id,
                        slug: 'details',
                      })}
                    />
                  )}
                  {values.isWalletWithdrawal ? (
                    <ContentField label='Amount'>
                      <div className='flex w-full items-center gap-3'>
                        {values.isOtherCurrency && (
                          <input
                            name='amountChecked'
                            type='radio'
                            className='h-4 w-4 shrink-0'
                            disabled={!isEdit}
                            checked={Boolean(values.amountChecked)}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                              setFieldValue('amountChecked', event.target.checked)
                              setFieldValue('otherAmountChecked', false)
                            }}
                          />
                        )}
                        <Input
                          type='number'
                          min='0'
                          step='any'
                          value={values.amount ?? ''}
                          readOnly={values.isOtherCurrency ? !values.amountChecked : !isEdit}
                          disabled={values.isOtherCurrency ? !values.amountChecked : !isEdit}
                          onChange={(event) => props.setFieldValue('amount', event.target.value)}
                        />
                      </div>
                    </ContentField>
                  ) : (
                    <FormField label='Amount' name='amount' type='number' readOnly={!isEdit} />
                  )}
                  {transactionNotes.length > 0 && (
                    <ContentField label='Transaction Notes'>
                      <div className='w-full'>
                        <NoteTextList transactionNotes={transactionNotes} />
                      </div>
                    </ContentField>
                  )}
                  {values.currency && (
                    <FormReadonlyField label='Currency' value={values.currency} />
                  )}
                  {values.isOtherCurrency && (
                    <FormReadonlyField label='Rate' value={values.currencyRate} />
                  )}
                  {values.isOtherCurrency && values.isWalletWithdrawal ? (
                    <ContentField label='Original Amount'>
                      <div className='flex w-full items-center gap-3'>
                        <input
                          name='otherAmountChanged'
                          type='radio'
                          className='h-4 w-4 shrink-0'
                          disabled={!isEdit}
                          checked={Boolean(values.otherAmountChecked)}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setFieldValue('otherAmountChecked', event.target.checked)
                            setFieldValue('amountChecked', false)
                          }}
                        />
                        <Input
                          type='number'
                          min='0'
                          step='any'
                          value={values.otherAmount ?? ''}
                          readOnly={!values.otherAmountChecked}
                          disabled={!values.otherAmountChecked}
                          onChange={(event) =>
                            props.setFieldValue('otherAmount', Number(event.target.value))
                          }
                        />
                      </div>
                    </ContentField>
                  ) : (
                    <FormReadonlyField label='Original Amount' value={values.otherAmount} />
                  )}
                  {values.isOtherCurrency && (
                    <FormReadonlyField label='Original Currency' value={values.otherCurrency} />
                  )}
                </DataCard>

                <DataCard title='Other'>
                  <FormTextareaField
                    label='Client Comment'
                    name='comment'
                    readOnly={!isEdit}
                    maxLength={130}
                  />
                  {transaction.reverseTransaction && (
                    <FormReadonlyField
                      label='Reverse Transaction'
                      value={transaction.reverseTransaction.name}
                      link={generatePath('/transactions/:id', {
                        id: transaction.reverseTransaction.id,
                      })}
                    />
                  )}
                  {transaction.reversedTransactions?.length ? (
                    <ContentField label='Reversed Transactions'>
                      <div className='w-full rounded-md border border-input bg-muted/30 px-3 py-2 text-sm'>
                        <ul className='list-disc pl-4'>
                          {transaction.reversedTransactions.map((reversedTransaction) => (
                            <li key={reversedTransaction.id}>
                              <Link
                                to={{
                                  pathname: generatePath('/transactions/:id', {
                                    id: reversedTransaction.id,
                                  }),
                                }}
                                className='font-medium text-primary hover:underline'
                              >
                                {reversedTransaction.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </ContentField>
                  ) : null}
                </DataCard>
              </DataColumn>

              <DataColumn>
                <DataCard title='Transaction Details'>
                  {values.wallet?.walletType?.name && (
                    <FormReadonlyField label='Wallet Type' value={values.wallet.walletType.name} />
                  )}
                  {values.wallet && (
                    <FormReadonlyField
                      label='Wallet'
                      value={values.wallet.name}
                      link={generatePath(links.recordsWallets.detailsUrl as string, {
                        id: values.wallet.id,
                        slug: 'details',
                      })}
                    />
                  )}
                  {isEdit ? (
                    <FormFieldLayout name='paymentProviderId' label='Payment Provider'>
                      <Select
                        value={values.paymentProviderId || undefined}
                        onChange={(value) => {
                          const nextValue = Array.isArray(value) ? value[0] : value
                          const resolvedValue = nextValue?.toString() ?? ''

                          setFieldValue('paymentProviderId', resolvedValue)

                          if (resolvedValue) {
                            getPaymentProviderFields(resolvedValue)
                          }
                        }}
                        options={paymentProviderOptions}
                        showSearch
                        fullWidth
                      />
                    </FormFieldLayout>
                  ) : (
                    <FormReadonlyField
                      label='Payment Provider'
                      value={selectedPaymentProviderName}
                    />
                  )}
                  <ContentField label='Connected To Service'>
                    <div className='flex min-h-9 items-center'>
                      <CheckboxSwitch
                        name='connectedToService'
                        initialValue={values.connectedToService}
                        disabled={!isEdit}
                        onChange={(value) => setFieldValue('connectedToService', value)}
                      />
                    </div>
                  </ContentField>
                </DataCard>

                <DataCard title='Details'>
                  {values.providerResponse && (
                    <FormField
                      label='Provider Response'
                      name='providerResponse'
                      readOnly={!isEdit}
                    />
                  )}
                  {values.providerReference && (
                    <FormField
                      label='Provider Reference'
                      name='providerReference'
                      readOnly={!isEdit}
                    />
                  )}
                  <FormFieldLayout name='externalAccount' label='External Account'>
                    <div className='flex w-full flex-col gap-2 sm:flex-row sm:items-center'>
                      <Input
                        id='externalAccount'
                        className='flex-1'
                        value={values.externalAccount ?? ''}
                        readOnly={!isEdit}
                        disabled={!isEdit}
                        onChange={(event) => setFieldValue('externalAccount', event.target.value)}
                      />
                      {values.bankAccountStatus && props.client && (
                        <Link
                          target='_blank'
                          rel='noopener noreferrer'
                          to={{
                            pathname: generatePath(links.recordsClients.detailsUrl as string, {
                              id: props.client.id,
                              slug: 'payment-details',
                            }),
                          }}
                          className='text-sm font-medium text-primary hover:underline'
                        >
                          {values.bankAccountStatus.name}
                        </Link>
                      )}
                    </div>
                  </FormFieldLayout>
                  {values.paymentCurrencyId && (
                    <FormReadonlyField label='Payment Currency' value={values.paymentCurrencyId} />
                  )}
                  {values.updateDetails.map((detail) => (
                    <FormField
                      key={detail.key}
                      label={detail.key}
                      name={detail.label}
                      value={detail.value ?? ''}
                      readOnly={!isEdit}
                      onChange={(event) => {
                        setFieldValue(
                          'updateDetails',
                          [
                            ...values.updateDetails.filter((item) => item.key !== detail.key),
                            {
                              ...detail,
                              value: event.target.value,
                            },
                          ].sort((a, b) => stringComparator(a.key, b.key))
                        )
                      }}
                    />
                  ))}
                </DataCard>
              </DataColumn>
            </DataGrid>
          </div>
        </Form>
      </div>
    </MainTemplate>
  )
}

function dataToSubmit(
  values: TransactionDetailsWalletDepositWithdrawalFormFormValues,
  transaction: TransactionDto
): FormSubmitValues<UpdateWalletDepositWithdrawalTransfer> {
  const details = []
  const { updateDetails } = values
  if (values.externalAccount) {
    details.push({ name: 'externalAccount', value: values.externalAccount })
  }
  if (values.providerReference) {
    details.push({ name: 'providerReference', value: values.providerReference })
  }
  if (values.providerResponse) {
    details.push({ name: 'providerResponse', value: values.providerResponse })
  }
  if (updateDetails.length) {
    updateDetails.forEach((d) => details.push({ name: d.label, value: d.value }))
  }
  // TEMP disable it for Deposits...
  if (values.isWalletWithdrawal) {
    let amount: number = Number.parseFloat((values.amount || 0).toString())
    let originalAmount: number | undefined
    if (values.isOtherCurrency) {
      amount = values.amountChecked
        ? values.amount
        : (getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.amount as number)
    }

    if (values.isOtherCurrency) {
      originalAmount = values.otherAmountChecked
        ? values.otherAmount
        : getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.otherAmount
    }

    const obj = omitEmpty({
      amount,
      originalAmount,
      comment: values.comment,
      connectedToService: values.connectedToService,
      paymentProviderId: values.paymentProviderId,
      details,
    })

    return {
      ...obj,
      details: obj.details || [],
    }
  } else {
    return {
      comment: values.comment,
      connectedToService: values.connectedToService,
      // referenceId: values.referenceId,
      // transactionId: values.id,
      originalAmount: values.amount,
      paymentProviderId: values.paymentProviderId,
      details,
    }
  }
}

export interface TransactionDetailsWalletDepositWithdrawalGeneralProps {
  transaction: TransactionDto
  client: { id: string; name: string } | undefined
  permissions: PermissionDto[]
  notes: TransactionNoteDto[]
  sendBankStatementNeededCCCoveredIBEmailRequest(): void
  sendBankStatementNeededDepositConfirmationEmailRequest(): void
  setActiveTab: (tabName: string) => void
  setAuditLogModal(value: boolean): void
  setCustomizeModal(value: boolean): void
  setProviderValidationModal(value: boolean): void
}

interface OuterProps extends TransactionDetailsWalletDepositWithdrawalGeneralProps {
  isEdit: boolean
  paymentProviders: PaymentMethodDto[]
  alternativesPaymentProviders: PaymentProviderAlternativeDto[]

  onSubmit(
    values: FormSubmitValues<UpdateWalletDepositWithdrawalTransfer>,
    type: 'deposit' | 'withdraw'
  ): void

  setEdit(val: boolean): void

  getPaymentProviderFields: (paymentProviderId: string) => Promise<void>
  paymentProviderFields: PaymentProviderFields[]
  loading: boolean
}

export const TransactionDetailsWalletDepositWithdrawalGeneralForm = withFormik<
  OuterProps,
  TransactionDetailsWalletDepositWithdrawalFormFormValues
>({
  mapPropsToValues: ({ transaction, paymentProviders }) => {
    const walletTransaction = getCreditCreditOrFirstOneWallet(transaction.walletTransactions)
    const paymentProvider = paymentProviders.find(
      (x) => x.id === walletTransaction?.paymentProviderTransaction?.paymentProvider.id
    )

    const isBankTransaction = paymentProvider?.categoryId === PaymentMethodCategory.Bank
    const isSkrillPaymentProviderType = paymentProvider?.text === PaymentProviderType.Skrill

    const isWalletWithdrawal = transaction.type.id === TransactionType.WalletWithdrawal
    const isOtherCurrency =
      walletTransaction?.otherCurrency.id !== walletTransaction?.wallet.currency.id

    const providerReference =
      walletTransaction?.paymentProviderTransaction?.response?.update?.providerReference
    const providerResponse =
      walletTransaction?.paymentProviderTransaction?.response?.update?.providerResponse
    const externalAccount =
      walletTransaction?.paymentProviderTransaction?.response?.update?.externalAccount
    const paymentCurrencyId =
      walletTransaction?.paymentProviderTransaction?.response?.update?.paymentCurrencyId
    const connectedToService = walletTransaction?.paymentProviderTransaction?.isConnectedToService
    const bankAccountStatus =
      walletTransaction?.paymentProviderTransaction?.clientBankAccountDetails?.status

    const updateDetails = Object.entries(
      walletTransaction?.paymentProviderTransaction?.response?.update?.details || {}
    ).map(([key, value]) => ({
      key: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z]+)*([A-Z][a-z])/g, '$1 $2'),
      value: typeof value === 'object' && value !== null ? JSON.stringify(value) : value,
      label: key,
    }))

    return {
      id: transaction.id,
      referenceId: transaction.referenceId,
      comment: transaction.comment,
      amount: getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.amount as number,
      wallet: getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.wallet,
      walletTransaction,
      isBankTransaction,
      isSkrillPaymentProviderType,
      isWalletWithdrawal,
      providerReference,
      providerResponse,
      externalAccount,
      paymentCurrencyId,
      updateDetails: updateDetails
        .filter((x) => !!x.value)
        .sort((a, b) => stringComparator(a.key, b.key)),
      connectedToService: connectedToService || false,
      isOtherCurrency,
      otherCurrency: walletTransaction?.otherCurrency.id,
      currencyRate: getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.currencyRate,
      otherAmount: getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.otherAmount,
      otherAmountChecked: false,
      amountChecked: false,
      paymentProviderId: getCreditCreditOrFirstOneWallet(transaction.walletTransactions)
        ?.paymentProviderTransaction?.paymentProvider.id,
      currency: getCreditCreditOrFirstOneWallet(transaction.walletTransactions)?.wallet.currency.id,
      bankAccountStatus,
      // options: {},
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    const type =
      props.transaction.type.id === TransactionType.WalletDeposit ? 'deposit' : 'withdraw'

    try {
      await props.onSubmit(dataToSubmit(values, props.transaction), type)
      props.setEdit(false)
    } catch (error) {
      console.error(error)
      props.setEdit(true)
    } finally {
      setSubmitting(false)
    }
  },
  validate: () => {
    const errors: FormikErrors<WithdrawalFormValues> = {}

    return errors
  },
  enableReinitialize: true,
})(TransactionDetailsWalletDepositWithdrawalGeneralUI)
